<script setup>
import { reactiveOmit } from '@vueuse/core';
import { PopoverContent, PopoverPortal, useForwardPropsEmits } from 'reka-ui';
import { cn } from '@/lib/utils';

defineOptions({
	inheritAttrs: false,
});

const props = defineProps({
	forceMount: { type: Boolean, required: false },
	memoDependencies: { type: Array, required: false },
	side: { type: null, required: false },
	sideOffset: { type: Number, required: false, default: 4 },
	sideFlip: { type: Boolean, required: false },
	align: { type: null, required: false, default: 'center' },
	alignOffset: { type: Number, required: false },
	alignFlip: { type: Boolean, required: false },
	avoidCollisions: { type: Boolean, required: false },
	collisionBoundary: { type: null, required: false },
	collisionPadding: { type: [Number, Object], required: false },
	arrowPadding: { type: Number, required: false },
	hideShiftedArrow: { type: Boolean, required: false },
	sticky: { type: String, required: false },
	hideWhenDetached: { type: Boolean, required: false },
	positionStrategy: { type: String, required: false },
	updatePositionStrategy: { type: String, required: false },
	disableUpdateOnLayoutShift: { type: Boolean, required: false },
	prioritizePosition: { type: Boolean, required: false },
	reference: { type: null, required: false },
	dir: { type: String, required: false },
	asChild: { type: Boolean, required: false },
	as: { type: null, required: false },
	disableOutsidePointerEvents: { type: Boolean, required: false },
	class: {
		type: [Boolean, null, String, Object, Array],
		required: false,
		skipCheck: true,
	},
});
const emits = defineEmits([
	'escapeKeyDown',
	'pointerDownOutside',
	'focusOutside',
	'interactOutside',
	'openAutoFocus',
	'closeAutoFocus',
]);

const delegatedProps = reactiveOmit(props, 'class');

const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
	<PopoverPortal>
		<PopoverContent
			data-slot="popover-content"
			v-bind="{ ...$attrs, ...forwarded }"
			:class="
				cn(
					'z-50 flex w-72 max-w-(--reka-popover-content-available-width) origin-(--reka-popover-content-transform-origin) flex-col gap-2.5 rounded-lg bg-popover p-2.5 text-sm text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-hidden duration-100 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
					props.class,
				)
			"
		>
			<slot />
		</PopoverContent>
	</PopoverPortal>
</template>
